import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FoodSearchBar from '../components/FoodSearchBar';
import BarcodeScanner from '../components/BarcodeScanner';
import Toast from '../components/Toast';
import { searchFoods, addFoodLog, getRecentFoods, getFrequentFoods, getRestaurants, getRestaurantMenu } from '../utils/nutritionApi';

const RESTAURANT_ICONS = {
  "McDonald's": "\uD83C\uDF54",
  "Burger King": "\uD83C\uDF54",
  "Wendy's": "\uD83C\uDF54",
  "Chick-fil-A": "\uD83D\uDC14",
  "Taco Bell": "\uD83C\uDF2E",
  "Subway": "\uD83E\uDD6A",
  "Chipotle": "\uD83C\uDF2F",
  "Starbucks": "\u2615",
  "Dunkin'": "\uD83C\uDF69",
  "Panera Bread": "\uD83C\uDF5E",
  "Domino's": "\uD83C\uDF55",
  "Pizza Hut": "\uD83C\uDF55",
  "KFC": "\uD83C\uDF57",
  "Popeyes": "\uD83C\uDF57",
  "Five Guys": "\uD83C\uDF54",
};

function Search() {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [mealType, setMealType] = useState('snack');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [adding, setAdding] = useState(false);
  const [success, setSuccess] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('search');
  const [recentFoods, setRecentFoods] = useState([]);
  const [frequentFoods, setFrequentFoods] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState('all');
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [restaurantMenu, setRestaurantMenu] = useState([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  const handleBarcodeFound = (food) => {
    setShowScanner(false);
    setSelectedFood(food);
    setQuantity(1);
    setSuccess(false);
  };

  const fetchTabData = useCallback(async () => {
    setTabLoading(true);
    try {
      const [recent, frequent] = await Promise.all([
        getRecentFoods(),
        getFrequentFoods()
      ]);
      setRecentFoods(recent);
      setFrequentFoods(frequent);
    } catch (err) {
      console.error('Failed to load tab data:', err.message);
    } finally {
      setTabLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTabData();
  }, [fetchTabData]);

  // Load restaurants when Restaurants tab is selected
  useEffect(() => {
    if (activeTab === 'restaurants' && restaurants.length === 0) {
      setTabLoading(true);
      getRestaurants()
        .then(setRestaurants)
        .catch(err => console.error('Failed to load restaurants:', err.message))
        .finally(() => setTabLoading(false));
    }
  }, [activeTab, restaurants.length]);

  const handleSelectQuickFood = (food) => {
    setSelectedFood({
      name: food.food_name,
      calories: food.calories,
      protein: food.protein ? parseFloat(food.protein) : null,
      carbs: food.carbs ? parseFloat(food.carbs) : null,
      fat: food.fat ? parseFloat(food.fat) : null,
      servingSize: food.serving_size ? parseFloat(food.serving_size) : null,
      servingUnit: food.serving_unit,
      fdcId: food.api_food_id
    });
    setQuantity(1);
    setSuccess(false);
  };

  const handleSearch = useCallback(async (query) => {
    if (!query) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const data = await searchFoods(query);
      setResults(data);
    } catch (err) {
      console.error('Search failed:', err.message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelectFood = (food) => {
    setSelectedFood(food);
    setQuantity(1);
    setSuccess(false);
  };

  const handleClosePanel = () => {
    setSelectedFood(null);
    setSuccess(false);
  };

  const adjustQuantity = (delta) => {
    setQuantity(prev => {
      const next = Math.round((prev + delta) * 10) / 10;
      return next < 0.1 ? 0.1 : next;
    });
  };

  const handleAddToLog = async () => {
    if (!selectedFood) return;
    setAdding(true);
    try {
      await addFoodLog({
        food_name: selectedFood.name,
        calories: selectedFood.calories || 0,
        protein: selectedFood.protein || null,
        carbs: selectedFood.carbs || null,
        fat: selectedFood.fat || null,
        fiber: selectedFood.fiber || null,
        serving_size: selectedFood.servingSize || null,
        serving_unit: selectedFood.servingUnit || null,
        quantity,
        meal_type: mealType,
        date,
        api_food_id: selectedFood.fdcId ? String(selectedFood.fdcId) : null
      });
      setSuccess(true);
      setToast({ message: `${selectedFood.name} added to log`, type: 'success' });
    } catch (err) {
      console.error('Add failed:', err.message);
      setToast({ message: 'Failed to add food', type: 'error' });
    } finally {
      setAdding(false);
    }
  };

  const handleAddAnother = () => {
    setSelectedFood(null);
    setSuccess(false);
    if (activeTab !== 'search') fetchTabData();
  };

  const handleSelectRestaurant = async (name) => {
    setSelectedRestaurant(name);
    setMenuLoading(true);
    try {
      const menu = await getRestaurantMenu(name);
      setRestaurantMenu(menu);
    } catch (err) {
      console.error('Failed to load menu:', err.message);
      setRestaurantMenu([]);
    } finally {
      setMenuLoading(false);
    }
  };

  const handleBackToRestaurants = () => {
    setSelectedRestaurant(null);
    setRestaurantMenu([]);
  };

  // Calculated values based on quantity
  const calc = (val) => val != null ? Math.round(val * quantity * 10) / 10 : null;

  const switchTab = (t) => {
    setActiveTab(t);
    setSelectedFood(null);
    setSuccess(false);
    setSelectedRestaurant(null);
    setRestaurantMenu([]);
  };

  // Filter search results
  const filteredResults = searchFilter === 'all'
    ? results
    : results.filter(f => {
        if (searchFilter === 'restaurant') return f.dataType === 'Restaurant';
        if (searchFilter === 'custom') return f.dataType === 'Custom';
        if (searchFilter === 'usda') return f.dataType !== 'Restaurant' && f.dataType !== 'Custom';
        return true;
      });

  // Group restaurant menu by category
  const menuByCategory = restaurantMenu.reduce((acc, item) => {
    const cat = item.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  return (
    <div className="page search-page">
      <h2>Search Foods</h2>

      {/* Tabs */}
      <div className="search-tabs">
        <button className={`search-tab ${activeTab === 'search' ? 'active' : ''}`} onClick={() => switchTab('search')}>Search</button>
        <button className={`search-tab ${activeTab === 'restaurants' ? 'active' : ''}`} onClick={() => switchTab('restaurants')}>Restaurants</button>
        <button className={`search-tab ${activeTab === 'recent' ? 'active' : ''}`} onClick={() => switchTab('recent')}>Recent</button>
        <button className={`search-tab ${activeTab === 'frequent' ? 'active' : ''}`} onClick={() => switchTab('frequent')}>Frequent</button>
      </div>

      {/* Search Tab */}
      {activeTab === 'search' && (
        <>
          <div className="search-bar-row">
            <FoodSearchBar onSearch={handleSearch} autoFocus />
            <button
              className="barcode-scan-btn"
              onClick={() => setShowScanner(true)}
              title="Scan barcode"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <line x1="6" y1="8" x2="6" y2="16" />
                <line x1="9" y1="8" x2="9" y2="16" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="15" y1="8" x2="15" y2="16" />
                <line x1="18" y1="8" x2="18" y2="16" />
              </svg>
            </button>
          </div>

          {/* Filter chips - show after search */}
          {searched && results.length > 0 && !selectedFood && (
            <div className="search-filter-chips">
              {[
                { key: 'all', label: 'All' },
                { key: 'restaurant', label: '\uD83C\uDF54 Restaurants' },
                { key: 'usda', label: 'USDA & Branded' },
                { key: 'custom', label: 'Custom' },
              ].map(f => (
                <button
                  key={f.key}
                  className={`search-filter-chip ${searchFilter === f.key ? 'active' : ''}`}
                  onClick={() => setSearchFilter(f.key)}
                >
                  {f.label}
                  {f.key !== 'all' && (
                    <span className="search-filter-count">
                      {results.filter(r => {
                        if (f.key === 'restaurant') return r.dataType === 'Restaurant';
                        if (f.key === 'custom') return r.dataType === 'Custom';
                        if (f.key === 'usda') return r.dataType !== 'Restaurant' && r.dataType !== 'Custom';
                        return true;
                      }).length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {loading && (
            <div className="search-loading">
              <div className="loading-spinner"></div>
            </div>
          )}
        </>
      )}

      {/* Recent/Frequent/Restaurants tabs loading */}
      {activeTab !== 'search' && tabLoading && (
        <div className="search-loading">
          <div className="loading-spinner"></div>
        </div>
      )}

      {/* Restaurants Tab - Browse */}
      {activeTab === 'restaurants' && !tabLoading && !selectedFood && !selectedRestaurant && (
        <div className="restaurant-browse">
          <p className="restaurant-browse-hint">Tap a restaurant to see its full menu</p>
          <div className="restaurant-grid">
            {restaurants.map(r => (
              <button
                key={r.restaurant_name}
                className="restaurant-card"
                onClick={() => handleSelectRestaurant(r.restaurant_name)}
              >
                <span className="restaurant-card-icon">{RESTAURANT_ICONS[r.restaurant_name] || '\uD83C\uDF7D\uFE0F'}</span>
                <span className="restaurant-card-name">{r.restaurant_name}</span>
                <span className="restaurant-card-count">{r.item_count} items</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Restaurant Menu View */}
      {activeTab === 'restaurants' && selectedRestaurant && !selectedFood && (
        <div className="restaurant-menu">
          <button className="add-panel-close" onClick={handleBackToRestaurants}>{'\u2190'} All Restaurants</button>
          <div className="restaurant-menu-header">
            <span className="restaurant-menu-icon">{RESTAURANT_ICONS[selectedRestaurant] || '\uD83C\uDF7D\uFE0F'}</span>
            <h3>{selectedRestaurant}</h3>
          </div>

          {menuLoading && (
            <div className="search-loading">
              <div className="loading-spinner"></div>
            </div>
          )}

          {!menuLoading && Object.keys(menuByCategory).map(cat => (
            <div key={cat} className="restaurant-menu-category">
              <h4 className="restaurant-menu-cat-label">{cat}</h4>
              <div className="search-results">
                {menuByCategory[cat].map(food => (
                  <button
                    key={food.fdcId}
                    className="search-result-item"
                    onClick={() => handleSelectFood(food)}
                  >
                    <div className="search-result-info">
                      <span className="search-result-name">{food.name}</span>
                      <span className="search-result-type type-restaurant">
                        {food.servingUnit}
                      </span>
                    </div>
                    <div className="search-result-cal-wrap">
                      <span className="search-result-cal">
                        {food.calories != null ? food.calories : '\u2014'}
                        <small> cal</small>
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent Tab */}
      {activeTab === 'recent' && !tabLoading && !selectedFood && (
        <div className="qa-food-list">
          {recentFoods.length === 0 ? (
            <div className="qa-empty">
              <p className="text-muted">No recent foods yet. Start logging to see them here.</p>
            </div>
          ) : (
            recentFoods.map((food, i) => (
              <button key={`${food.food_name}-${i}`} className="qa-food-item" onClick={() => handleSelectQuickFood(food)}>
                <div className="qa-food-info">
                  <span className="qa-food-name">{food.food_name}</span>
                  {food.serving_size && (
                    <span className="qa-food-meta">{food.serving_size}{food.serving_unit || 'g'}</span>
                  )}
                </div>
                <div className="qa-food-cal">{food.calories != null ? food.calories : '\u2014'}<small> cal</small></div>
              </button>
            ))
          )}
        </div>
      )}

      {/* Frequent Tab */}
      {activeTab === 'frequent' && !tabLoading && !selectedFood && (
        <div className="qa-food-list">
          {frequentFoods.length === 0 ? (
            <div className="qa-empty">
              <p className="text-muted">No frequent foods yet. Log foods a few times to see them here.</p>
            </div>
          ) : (
            frequentFoods.map((food, i) => (
              <button key={`${food.food_name}-${i}`} className="qa-food-item" onClick={() => handleSelectQuickFood(food)}>
                <div className="qa-food-info">
                  <span className="qa-food-name">{food.food_name}</span>
                  <span className="qa-food-meta">
                    {food.serving_size && `${food.serving_size}${food.serving_unit || 'g'}`}
                    <span className="qa-food-count">logged {food.count} time{food.count !== 1 ? 's' : ''}</span>
                  </span>
                </div>
                <div className="qa-food-cal">{food.calories != null ? food.calories : '\u2014'}<small> cal</small></div>
              </button>
            ))
          )}
        </div>
      )}

      {/* Search Results */}
      {activeTab === 'search' && !loading && !selectedFood && (
        <div className="search-results">
          {filteredResults.map((food) => {
            const isCustom = food.dataType === 'Custom';
            const isBranded = food.dataType === 'Branded';
            const isRestaurant = food.dataType === 'Restaurant';
            const typeLabel = isCustom
              ? 'Custom'
              : isRestaurant
                ? `${RESTAURANT_ICONS[food.brandOwner] || '\uD83C\uDF54'} ${food.brandOwner}`
                : isBranded
                  ? food.brandOwner
                  : 'USDA Standard';
            const typeClass = isCustom
              ? 'type-custom'
              : isRestaurant
                ? 'type-restaurant'
                : isBranded
                  ? 'type-branded'
                  : 'type-generic';
            const servingText = isRestaurant
              ? food.servingUnit
              : food.servingSize
                ? `per ${food.servingSize}${food.servingUnit || 'g'}`
                : null;

            return (
              <button
                key={food.fdcId}
                className="search-result-item"
                onClick={() => handleSelectFood(food)}
              >
                <div className="search-result-info">
                  <span className="search-result-name">{food.name}</span>
                  <span className={`search-result-type ${typeClass}`}>
                    {typeLabel}
                  </span>
                  {servingText && (
                    <span className="search-result-serving">{servingText}</span>
                  )}
                </div>
                <div className="search-result-cal-wrap">
                  <span className="search-result-cal">
                    {food.calories != null ? food.calories : '\u2014'}
                    <small> cal</small>
                  </span>
                  {servingText && (
                    <span className="search-result-cal-per">{servingText}</span>
                  )}
                </div>
              </button>
            );
          })}

          {searched && !loading && filteredResults.length === 0 && (
            <p className="text-muted search-empty">
              {results.length > 0 && searchFilter !== 'all'
                ? 'No results in this category. Try "All" filter.'
                : 'No foods found. Try a different search.'}
            </p>
          )}
        </div>
      )}

      {/* Add to Log Panel */}
      {selectedFood && !success && (
        <div className="add-panel">
          <button className="add-panel-close" onClick={handleClosePanel}>{'\u2190'} Back</button>

          <div className="add-panel-header">
            {selectedFood.imageUrl && (
              <img
                src={selectedFood.imageUrl}
                alt={selectedFood.name}
                className="add-panel-image"
              />
            )}
            <div>
              <h3>{selectedFood.name}</h3>
              {selectedFood.brandOwner && (
                <p className={`text-muted ${selectedFood.dataType === 'Restaurant' ? 'restaurant-label' : ''}`}>
                  {selectedFood.dataType === 'Restaurant' && `${RESTAURANT_ICONS[selectedFood.brandOwner] || '\uD83C\uDF54'} `}
                  {selectedFood.dataType === 'Barcode' && '\u{1F4F7} '}
                  {selectedFood.brandOwner}
                </p>
              )}
            </div>
          </div>

          {/* Nutrition preview */}
          <div className="add-panel-nutrition">
            <div className="add-panel-cal">
              <span className="add-panel-cal-value">{calc(selectedFood.calories) || 0}</span>
              <span className="add-panel-cal-label">calories</span>
            </div>
            <div className="add-panel-macros">
              {calc(selectedFood.protein) != null && (
                <span className="add-panel-macro">P: {calc(selectedFood.protein)}g</span>
              )}
              {calc(selectedFood.carbs) != null && (
                <span className="add-panel-macro">C: {calc(selectedFood.carbs)}g</span>
              )}
              {calc(selectedFood.fat) != null && (
                <span className="add-panel-macro">F: {calc(selectedFood.fat)}g</span>
              )}
            </div>
          </div>

          {/* Serving info */}
          {selectedFood.servingSize && (
            <p className="add-panel-serving">
              Serving: {selectedFood.servingSize}{selectedFood.servingUnit || 'g'}
            </p>
          )}

          {/* Quantity */}
          <div className="add-panel-field">
            <label>Quantity</label>
            <div className="quantity-control">
              <button className="qty-btn" onClick={() => adjustQuantity(-0.5)}>{'\u2212'}</button>
              <input
                type="number"
                className="qty-value"
                value={quantity}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!isNaN(v) && v > 0) setQuantity(v);
                }}
                min="0.1"
                step="0.5"
              />
              <button className="qty-btn" onClick={() => adjustQuantity(0.5)}>+</button>
            </div>
          </div>

          {/* Meal Type */}
          <div className="add-panel-field">
            <label>Meal</label>
            <div className="meal-selector">
              {['breakfast', 'lunch', 'dinner', 'snack'].map(type => (
                <button
                  key={type}
                  className={`meal-btn ${mealType === type ? 'active' : ''}`}
                  onClick={() => setMealType(type)}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Date */}
          <div className="add-panel-field">
            <label>Date</label>
            <input
              type="date"
              className="date-input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <button
            className="btn btn-primary btn-full"
            onClick={handleAddToLog}
            disabled={adding}
          >
            {adding ? 'Adding...' : 'Add to Log'}
          </button>
        </div>
      )}

      {/* Success */}
      {success && (
        <div className="add-success">
          <div className="add-success-icon">&#10003;</div>
          <h3>Added to Log!</h3>
          <p className="text-muted">
            {selectedFood?.name} — {calc(selectedFood?.calories)} cal
          </p>
          <div className="add-success-actions">
            <button className="btn btn-outline" onClick={handleAddAnother}>
              Add Another
            </button>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </button>
          </div>
        </div>
      )}

      {/* Barcode Scanner */}
      {showScanner && (
        <BarcodeScanner
          onFoodFound={handleBarcodeFound}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default Search;
